import { useMemo } from "react";
import { useTranslation } from "../../i18n";
import { statusCategory } from "./laneStatusConfig";
import { todayISO } from "./mockLaneData";
import type { Lane } from "./useLanes";

export type KpiBucket =
    | "total"
    | "on-auction"
    | "closing-today"
    | "outbound"
    | "idle"
    | "unprocessed";

interface LaneKpiStripProps {
    lanes: Lane[];
    active: KpiBucket | null;
    onChange: (bucket: KpiBucket | null) => void;
}

interface TileSpec {
    bucket: KpiBucket;
    labelKey: string;
    dot: string;
}

// Family-coloured dot in the top-left of each tile. Total picks a
// neutral ink shade; the rest borrow their family's accent bar token
// so the visual language stays consistent with the row accents and
// status pills.
const TILES: TileSpec[] = [
    { bucket: "total", labelKey: "lanes_kpi_total", dot: "var(--ink-400)" },
    { bucket: "on-auction", labelKey: "lanes_kpi_on_auction", dot: "var(--status-on-auction-bar)" },
    { bucket: "closing-today", labelKey: "lanes_kpi_closing_today", dot: "var(--status-auctions-ended-bar)" },
    { bucket: "outbound", labelKey: "lanes_kpi_outbound", dot: "var(--status-apc-bar)" },
    { bucket: "idle", labelKey: "lanes_kpi_idle", dot: "var(--status-lane-empty-bar)" },
    { bucket: "unprocessed", labelKey: "lanes_kpi_unprocessed", dot: "var(--status-unprocessed-stock-bar)" },
];

export const bucketMatches = (lane: Lane, bucket: KpiBucket): boolean => {
    switch (bucket) {
        case "total": return true;
        case "on-auction": return statusCategory(lane.status) === "auction";
        case "closing-today": return lane.auction_end_date === todayISO();
        case "outbound": return statusCategory(lane.status) === "outbound";
        case "idle": return statusCategory(lane.status) === "idle";
        case "unprocessed": return statusCategory(lane.status) === "unprocessed";
    }
};

const LaneKpiStrip = ({ lanes, active, onChange }: LaneKpiStripProps) => {
    const { t } = useTranslation();

    const counts = useMemo(() => {
        const out: Record<KpiBucket, number> = {
            total: lanes.length,
            "on-auction": 0,
            "closing-today": 0,
            outbound: 0,
            idle: 0,
            unprocessed: 0,
        };
        const todays = todayISO();
        for (const l of lanes) {
            const cat = statusCategory(l.status);
            if (cat === "auction") out["on-auction"]++;
            if (cat === "outbound") out["outbound"]++;
            if (cat === "idle") out["idle"]++;
            if (cat === "unprocessed") out["unprocessed"]++;
            if (l.auction_end_date === todays) out["closing-today"]++;
        }
        return out;
    }, [lanes]);

    return (
        <div className="lanes-kpi-strip">
            {TILES.map(tile => {
                const isActive = active === tile.bucket;
                return (
                    <button
                        key={tile.bucket}
                        type="button"
                        className={`lanes-kpi-tile${isActive ? " lanes-kpi-tile--active" : ""}`}
                        onClick={() => onChange(isActive ? null : tile.bucket)}
                        aria-pressed={isActive}
                    >
                        <span className="lanes-kpi-tile-dot" style={{ background: tile.dot }} />
                        <span className="lanes-kpi-tile-count">{counts[tile.bucket]}</span>
                        <span className="lanes-kpi-tile-label">{t(tile.labelKey)}</span>
                    </button>
                );
            })}
        </div>
    );
};

export default LaneKpiStrip;
